import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import GenericDivider from '../../components/GenericDivider';
import SearchableList from '../../components/SearchableList';
import useModalRef, { ModalRef } from '../../hooks/useModalRef';
import Teacher from '../../models/Teacher';
import MutableListItemOptions from '../../components/MutableListItemOptions';
import { Link } from 'react-router-dom';
import useClasses from '../../services/swr/useClasses';
import useFilter from '../../hooks/useFilter';
import useToast from '../../hooks/useToast';
import useTeachers from '../../services/swr/useTeachers';
import {
  ListItemContainer,
  ModalContainer,
} from './AddStudentsToClassModal.styles';
import { TeachersListControls } from '../CourseDetailsPage/AddTeachersToCourseModal.styles';
import GenericSelectableList from '../../components/GenericSelectableList';

interface ComponentProps {
  classId: string;
}

const ClassTeachersList = ({ classId }: ComponentProps) => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const { getClassTeachers } = useClasses();
  const addTeachersModalRef = useModalRef();

  const handleSearch = (search: string, teacher: Teacher) => {
    const treatedTeacherName = teacher.profile.name?.toLowerCase();
    const treatedTeacherEmail = teacher.profile.email.toLowerCase();

    const match =
      (treatedTeacherName && treatedTeacherName.includes(search)) ||
      treatedTeacherEmail.includes(search);

    return match;
  };

  useEffect(() => {
    (async () => {
      const teachers = await getClassTeachers(classId);
      setTeachers(teachers);
    })();
  }, [classId, getClassTeachers]);

  return (
    <>
      <SearchableList
        dataArray={teachers}
        searchMatches={handleSearch}
        onAdd={() => addTeachersModalRef.current?.openModal()}
      >
        {({ data }) => (
          <>
            {data.length > 0 ? (
              <ul className="list-none space-y-6">
                {data.map((teacher, index) => (
                  <Fragment key={teacher.id}>
                    <ClassTeachersListItem data={teacher} classId={classId} />
                    {index < data.length - 1 && <GenericDivider />}
                  </Fragment>
                ))}
              </ul>
            ) : (
              <p>Nenhum professor encontrado</p>
            )}
          </>
        )}
      </SearchableList>

      <AddTeachersModal
        ref={addTeachersModalRef}
        classId={classId}
        teachersInClass={teachers}
      />
    </>
  );
};

export default ClassTeachersList;

type ClassTeachersProps = {
  data: Teacher;
  classId: string;
};

const ClassTeachersListItem = ({
  data: teacherInfo,
  classId,
}: ClassTeachersProps) => {
  const { removeTeacherFromClass } = useClasses();

  async function handleRemoveTeacher() {
    await removeTeacherFromClass(classId, teacherInfo.id);
  }

  return (
    <>
      <li className="flex items-center justify-start">
        <Link
          to={`/teacher-details/${teacherInfo.id}`}
          className="flex justify-start flex-col font-sans text-xl no-underline"
        >
          <p className="font-medium">{teacherInfo.profile.name}</p>
          <p className="teacher-email">{teacherInfo.profile.email}</p>
        </Link>
        <MutableListItemOptions
          className="ml-auto"
          objectName={teacherInfo.profile.name || teacherInfo.profile.email}
          onEdit={() => {}}
          // onRemove={() => {}}
          onRemove={() => handleRemoveTeacher()}
        />
      </li>
    </>
  );
};

type AddTeachersModalProps = {
  classId: string;
  teachersInClass: Teacher[];
};

const AddTeachersModal = React.forwardRef(
  ({ classId, teachersInClass }: AddTeachersModalProps, ref) => {
    const { addToast } = useToast();
    const { teachers } = useTeachers();
    const { addTeacherToClass } = useClasses();
    const teachersNotInCourse = useMemo<Teacher[]>(() => {
      const courseTeachersIds = teachersInClass.map(teacher => teacher.id);
      const teachersNotInCourse = teachers.filter(
        t => !courseTeachersIds.includes(t.id),
      );

      return teachersNotInCourse;
    }, [teachersInClass, teachers]);

    const {
      filter,
      clearFilter,
      filteredData: filteredTeachers,
    } = useFilter<Teacher>(teachersNotInCourse);

    const [isUsingSearch, setIsUsingSearch] = useState(false);

    const handleSearch = useCallback(
      async (search: string) => {
        const treatedSearch = search.toLowerCase().trim();

        filter(teacher => {
          const treatedTeacher = Object.values(teacher.profile)
            .join('')
            .toLowerCase();
          console.log(treatedTeacher);
          if (treatedTeacher.includes(treatedSearch)) return true;

          return false;
        });

        setIsUsingSearch(true);
      },
      [filter],
    );

    const handleSubmission = useCallback(
      (teachers: Teacher[]) => {
        if (teachers.length === 0) {
          addToast({
            type: 'error',
            title: 'Nenhum professor foi selecionado',
            description:
              'Por favor selecione pelo menos um professor para adicionar ao curso',
          });

          return;
        }

        addTeacherToClass(
          classId,
          teachers.map(teacher => teacher.id),
        );

        if (ref) {
          (ref as ModalRef).current?.closeModal();
        }
      },
      [addTeacherToClass, addToast, ref, classId],
    );

    return (
      <ModalContainer ref={ref}>
        <TeachersListControls
          onSearch={handleSearch}
          onClearSearch={clearFilter}
        />

        <GenericSelectableList
          dataArray={isUsingSearch ? filteredTeachers : teachersNotInCourse}
          confirmSelectionButtonText="Adicionar Professores"
          onConfirmSelection={handleSubmission}
          listItemComponent={TeacherListItem}
        ></GenericSelectableList>
      </ModalContainer>
    );
  },
);

AddTeachersModal.displayName = 'AddTeachersModal';

const TeacherListItem = ({ data }: { data: Teacher }) => {
  return (
    <ListItemContainer>
      <p className="font-medium">{data.profile.name}</p>
      <p className="teacher-email">{data.profile.email}</p>
    </ListItemContainer>
  );
};
